'use client';
/* eslint-disable tailwindcss/no-arbitrary-value */
/* eslint-disable tailwindcss/no-custom-classname */
/* eslint-disable tailwindcss/no-unnecessary-arbitrary-value */
/* eslint-disable tailwindcss/classnames-order */

import { useCtaUrl } from '@hyphengroup/cms-components/components/ApplyCta';
import { CmsImage } from '@hyphengroup/cms-components/components/CmsImage';
import { Icon } from '@hyphengroup/cms-components/components/Icon';
import ContentCopy from '@hyphengroup/cms-components/components/icons/ContentCopy';
import { RichText } from '@hyphengroup/cms-components/components/RichText';
import { useCopyWithFeedback } from '@hyphengroup/cms-components/hooks/useCopyWithFeedback';
import classNames from 'classnames';
import { FC } from 'react';
import { BrandId } from 'utils/override';
import { FlexibleHeroBlockBreadcrumb } from '../../FlexibleHeroBlockBreadcrumb';
import { mapProductHeroData, ProductHeroData } from '../../productHero';
import {
  AlignmentClasses,
  TextClasses,
  WideLayoutBreakpoint,
} from '../../types';
import { useFlexibleHeroBlockApplyClick } from '../../useFlexibleHeroBlockApplyClick';
import { useFlexibleHeroBlockTimestamp } from '../../useFlexibleHeroBlockTimestamp';
import {
  getCmsCallToAction,
  getProductCallToAction,
  getProductCardClasses,
} from '../../utils';
import { CtaButtonComponents } from './CtaButtonComponents';
import { modernLayoutTranslations } from './i18n';
import { RewardImage } from './RewardImage';
import { usePageDataContextOptional } from '$/(cms)/_modules/cmsService/pageDataService/pageDataProvider';
import {
  ComponentCtaButton,
  ComponentFlexibleHeroBlock,
  EnumFlexibleHeroBlockHeroType,
  EnumFlexibleHeroBlockTheme,
} from '$/(cms)/_modules/cmsService/types';
import { CallToActionButton } from '$/components/cms/components-internal/callToActionButton/CallToActionButton';
import { WrappedText } from '$/components/cms/components-internal/WrappedText';
import { getPaymentIconBadges } from '$/components/cms/components-user/FlexibleHeroBlock/Default/components/payment-icon-badges';
import { tr } from '$/components/cms/helpers';
import { getPageProductMetaConfig } from '$/components/cms/helpers/productMetaConfig';

type WideLayoutClassSet = {
  /** Root width cap when there is no side content. */
  narrowRootMaxWidth: string;
  /** Text column + aside switch from a stack to one row. */
  contentRow: string;
  /** Side content stacked under the headline — hidden once the row kicks in. */
  stackedSideContent: string;
  /** Side content as an aside — shown once the row kicks in. */
  asideSideContent: string;
  asideProductWidth: string;
  asideDefaultWidth: string;
  productHeadline: string;
  productBodyVisibility: string;
  bodyMaxWidth: string;
  productPaymentsGap: string;
  productPaymentsLabel: string;
  productPromoText: string;
  promoCopyButtonSize: string;
  keyStatLabelAlign: string;
  keyStatValueAlign: string;
  /** Desktop chrome of the key-stat value row (left border + padding), CMS-1811. */
  keyStatValueFrame: string;
  productImageRounding: string;
  /** `sizes` hint for the product image — slot widths per viewport. */
  productImageSizes: { _: string; sm?: number; md?: number };
};

/**
 * Every viewport-breakpoint-dependent class, keyed by the breakpoint at which
 * the hero leaves the stacked (mobile) layout for the wide two-column one.
 * `md` is the page hero — the current behaviour, byte-for-byte. `lg` is for
 * narrow panels whose own width trails the viewport by one breakpoint: the
 * #usePDP modal is a 448px panel on md viewports and 1080px from lg, so a
 * md-wide layout inside it squeezes the text column to nothing (CMS-1924).
 * Tailwind only picks up literal class strings, hence two parallel sets
 * instead of a computed prefix.
 */
const WIDE_LAYOUT_CLASSES: Record<WideLayoutBreakpoint, WideLayoutClassSet> = {
  md: {
    narrowRootMaxWidth:
      'md:w-general/narrow-content-max-width-1 md:max-w-general/narrow-content-max-width-1',
    contentRow: 'md:flex-row md:items-center md:gap-6',
    stackedSideContent: 'md:hidden',
    asideSideContent: 'md:flex',
    asideProductWidth:
      'md:basis-[304px] md:max-w-[304px] lg:basis-[300px] lg:max-w-[300px]',
    asideDefaultWidth:
      'lg:max-w-[--general-4-col-width] lg:basis-[--general-4-col-width] md:max-w-[--general-3-col-width] md:basis-[--general-3-col-width]',
    productHeadline:
      'mhg-alpha-typography-display-3 md:mhg-alpha-typography-display-2',
    productBodyVisibility: 'hidden md:block',
    bodyMaxWidth: 'md:max-w-[--general-6-col-width]',
    productPaymentsGap: 'gap-[4px] md:gap-[8px]',
    productPaymentsLabel:
      'mhg-alpha-typography-caption md:mhg-alpha-typography-body-2-relaxed xl:mhg-alpha-typography-body-1-relaxed',
    productPromoText:
      'mhg-alpha-typography-caption md:mhg-alpha-typography-body-2-relaxed xl:mhg-alpha-typography-body-1',
    promoCopyButtonSize: 'size-[16px] md:size-auto',
    keyStatLabelAlign: 'text-center md:text-left',
    keyStatValueAlign: 'justify-center md:justify-start',
    keyStatValueFrame:
      'md:border-l-2 md:border-borders/form-default md:pl-spacing/gap/soft-12px',
    productImageRounding: 'rounded-radius/button md:rounded-radius/card-inner',
    // Full content width below 768px, the ~264px aside slot from `md` up.
    productImageSizes: { _: '100vw', sm: 264 },
  },
  lg: {
    narrowRootMaxWidth:
      'lg:w-general/narrow-content-max-width-1 lg:max-w-general/narrow-content-max-width-1',
    contentRow: 'lg:flex-row lg:items-center lg:gap-6',
    stackedSideContent: 'lg:hidden',
    asideSideContent: 'lg:flex',
    asideProductWidth: 'lg:basis-[300px] lg:max-w-[300px]',
    asideDefaultWidth:
      'lg:max-w-[--general-4-col-width] lg:basis-[--general-4-col-width]',
    productHeadline:
      'mhg-alpha-typography-display-3 lg:mhg-alpha-typography-display-2',
    productBodyVisibility: 'hidden lg:block',
    bodyMaxWidth: 'lg:max-w-[--general-6-col-width]',
    productPaymentsGap: 'gap-[4px] lg:gap-[8px]',
    productPaymentsLabel:
      'mhg-alpha-typography-caption lg:mhg-alpha-typography-body-2-relaxed xl:mhg-alpha-typography-body-1-relaxed',
    productPromoText:
      'mhg-alpha-typography-caption lg:mhg-alpha-typography-body-2-relaxed xl:mhg-alpha-typography-body-1',
    promoCopyButtonSize: 'size-[16px] lg:size-auto',
    keyStatLabelAlign: 'text-center lg:text-left',
    keyStatValueAlign: 'justify-center lg:justify-start',
    keyStatValueFrame:
      'lg:border-l-2 lg:border-borders/form-default lg:pl-spacing/gap/soft-12px',
    productImageRounding: 'rounded-radius/button lg:rounded-radius/card-inner',
    // The stacked slot is the 448px panel from 768px, the aside slot from `lg`.
    productImageSizes: { _: '100vw', sm: 448, md: 264 },
  },
};

type NonCampaignSideContentProps = {
  heroVariant: EnumFlexibleHeroBlockHeroType;
  imageDesktop: ComponentFlexibleHeroBlock['imageDesktop'];
  imageMobile: ComponentFlexibleHeroBlock['imageMobile'];
  headline?: string;
  lang: string;
  product: ComponentFlexibleHeroBlock['product'];
  productCallToAction: ComponentFlexibleHeroBlock['productCallToAction'];
  productHref?: string;
  /** See `FlexibleHeroBlockProps['ctaLinkOverride']`. */
  ctaLinkOverride?: string;
  productHeroData?: ProductHeroData;
  provider: ComponentFlexibleHeroBlock['provider'];
  providerCallToAction: ComponentFlexibleHeroBlock['providerCallToAction'];
  onApplyClick?: () => void;
  layout: WideLayoutClassSet;
  productCardThemeClasses: string;
  textClasses: TextClasses;
};

const NonCampaignSideContent = ({
  heroVariant,
  imageDesktop,
  imageMobile,
  headline,
  lang,
  product,
  productCallToAction,
  productHref,
  ctaLinkOverride,
  productHeroData,
  provider,
  providerCallToAction,
  onApplyClick,
  layout,
  productCardThemeClasses,
  textClasses,
}: NonCampaignSideContentProps): JSX.Element | null => {
  switch (heroVariant) {
    case EnumFlexibleHeroBlockHeroType.Cta:
    case EnumFlexibleHeroBlockHeroType.FormFieldAndImage:
    case EnumFlexibleHeroBlockHeroType.ImageOnly: {
      if (!imageDesktop && !imageMobile) {
        return null;
      }
      return (
        <RewardImage
          imageDesktop={imageDesktop}
          imageMobile={imageMobile}
          headline={headline}
        />
      );
    }
    case EnumFlexibleHeroBlockHeroType.Product: {
      const cta = getProductCallToAction(
        product,
        productCallToAction,
        tr('apply_now', lang),
        productHref,
        ctaLinkOverride,
      );
      const imageUrl = product?.product_image;
      const hasKeyStats = (productHeroData?.keyStats?.length ?? 0) > 0;
      const productTag = productHeroData?.productTag;

      if (!imageUrl && !cta && !hasKeyStats && !productTag) {
        return null;
      }

      // `backdrop-blur-frost*` (globals.common.css) carries the -webkit- prefix
      // Tailwind omits. The translucent fill doubles as the fallback where
      // `backdrop-filter` is unsupported, so there is no white flash (CMS-1811).
      return (
        <div
          className={classNames(
            'flex w-full flex-col gap-spacing/gap/soft-12px rounded-radius/card border px-spacing/gap/x py-spacing/gap/y-sm',
            productCardThemeClasses,
          )}
          data-component="flexible-hero-block-product-card"
        >
          {productTag && (
            <div className="w-full hidden md:block">
              <ProductTag productTag={productTag} />
            </div>
          )}
          {imageUrl && (
            <div
              className={classNames(
                'overflow-hidden',
                layout.productImageRounding,
              )}
            >
              {/*
               * CMS-1958: the 160px cap keeps portrait card art within the
               * landscape slot height; `object-contain` centers the drawn
               * image inside the box, so no orientation branch is needed.
               * The box itself stays `w-full` (not `w-auto`) on purpose:
               * the layout must be slot-driven, not driven by the uploaded
               * asset's pixel size, and the `width`/`height` ratio then
               * reserves the exact final box before load (no CLS).
               * Slot widths for `sizes` come from the layout set (CmsImage's
               * `sm` key = min-width 768px, `md` = 1280px): full content width
               * on mobile, the ~264px aside slot once the wide layout kicks in.
               */}
              <CmsImage
                src={imageUrl}
                alt={product?.product_name ?? product?.title ?? 'Product image'}
                width={320}
                height={200}
                sizes={layout.productImageSizes}
                className="h-auto max-h-[160px] w-full object-contain"
              />
            </div>
          )}
          {cta && <CallToActionButton cta={cta} onClick={onApplyClick} />}
          {productTag && (
            <div className="w-full block md:hidden">
              <ProductTag productTag={productTag} />
            </div>
          )}
          {productHeroData?.keyStats?.length ? (
            <div className="flex flex-col gap-spacing/gap/soft-8px">
              {productHeroData.keyStats.map((item) => (
                <div
                  key={item.key}
                  className="flex flex-col gap-spacing/gap/soft-4px"
                >
                  <div
                    className={classNames(
                      'mhg-alpha-typography-table-header',
                      textClasses.secondary,
                      layout.keyStatLabelAlign,
                    )}
                  >
                    {item.label}
                  </div>
                  {/*
                   * CMS-1811: primary and secondary values share the
                   * `breadcrumb` style in the theme's secondary colour on every
                   * viewport (ticket req. 3 / AC 3, "no navy remains"); the
                   * label is distinguished by weight.
                   * On desktop the value additionally sits in a left-bordered,
                   * left-aligned container.
                   */}
                  <div
                    className={classNames(
                      'flex flex-wrap items-center gap-spacing/gap/soft-4px',
                      layout.keyStatValueAlign,
                      layout.keyStatValueFrame,
                    )}
                  >
                    <span
                      className={classNames(
                        'mhg-alpha-typography-breadcrumb',
                        textClasses.secondary,
                      )}
                    >
                      {item.primaryValue}
                    </span>
                    {item.secondaryValue ? (
                      <span
                        className={classNames(
                          'mhg-alpha-typography-breadcrumb',
                          textClasses.secondary,
                        )}
                      >
                        {item.secondaryValue}
                      </span>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      );
    }
    case EnumFlexibleHeroBlockHeroType.Provider: {
      const cta = getCmsCallToAction(
        providerCallToAction,
        tr('apply_now', lang),
      );
      const imageUrl = provider?.providerImage;

      if (!imageUrl && !cta) {
        return null;
      }

      return (
        <div className="flex w-full flex-col items-center gap-[--spacing-gap-soft-12px]">
          {imageUrl && (
            <CmsImage
              unoptimized
              src={imageUrl}
              alt={provider?.providerName ?? 'Provider image'}
              width={240}
              height={120}
              className="h-auto w-full object-contain"
            />
          )}
          {cta && <CallToActionButton cta={cta} onClick={onApplyClick} />}
        </div>
      );
    }
    default:
      return null;
  }
};

const ProductTag = ({ productTag }: { productTag: string }) => {
  return (
    <div className="mhg-alpha-typography-badge w-full rounded-radius/button px-spacing/padding/soft-8px py-spacing/padding/soft-4px bg-fill/primary-light text-fill/primary-dark text-center">
      {productTag}
    </div>
  );
};

type ModernLayoutProps = {
  alignment: AlignmentClasses;
  buttons: ComponentCtaButton[];
  bodyCopy?: string;
  brand: BrandId;
  headline?: string;
  headingId?: string;
  isPageHeadline?: boolean;
  highlights?: ComponentFlexibleHeroBlock['highlights'];
  heroVariant: EnumFlexibleHeroBlockHeroType;
  imageDesktop: ComponentFlexibleHeroBlock['imageDesktop'];
  imageMobile: ComponentFlexibleHeroBlock['imageMobile'];
  keyStats?: ComponentFlexibleHeroBlock['keyStats'];
  lang: string;
  overline?: string;
  product: ComponentFlexibleHeroBlock['product'];
  productCallToAction: ComponentFlexibleHeroBlock['productCallToAction'];
  provider: ComponentFlexibleHeroBlock['provider'];
  providerCallToAction: ComponentFlexibleHeroBlock['providerCallToAction'];
  showBreadcrumbs?: boolean;
  showTimestamp?: boolean;
  textClasses: TextClasses;
  theme?: EnumFlexibleHeroBlockTheme;
  /** See {@link WIDE_LAYOUT_CLASSES}. Default `md` — the page-hero layout. */
  wideLayoutBreakpoint?: WideLayoutBreakpoint;
  /** See `FlexibleHeroBlockProps['ctaLinkOverride']`. */
  ctaLinkOverride?: string;
};

export const ModernLayout: FC<ModernLayoutProps> = ({
  alignment,
  buttons,
  bodyCopy,
  brand,
  headline,
  headingId,
  isPageHeadline = true,
  highlights,
  heroVariant,
  imageDesktop,
  imageMobile,
  keyStats,
  lang,
  overline,
  product,
  productCallToAction,
  provider,
  providerCallToAction,
  showBreadcrumbs,
  showTimestamp,
  textClasses,
  theme,
  wideLayoutBreakpoint = 'md',
  ctaLinkOverride,
}) => {
  // Lenient read: the hero also renders inside the #usePDP modal, which can
  // mount on routes without a PageDataProvider (apply-v3). Every consumer of
  // this context (the product meta config below, the timestamp, breadcrumbs)
  // is optional.
  const { pageData } = usePageDataContextOptional() ?? {};
  const promoCodeCopy = useCopyWithFeedback();
  const translations = modernLayoutTranslations.get(lang);
  const productHref = useCtaUrl({ product, ctaLinkOverride });

  // The Provider CTA is wired to the same handler; without a product it only
  // records the apply attribution (see useFlexibleHeroBlockApplyClick).
  const handleApplyClick = useFlexibleHeroBlockApplyClick(product);
  const timestamp = useFlexibleHeroBlockTimestamp(showTimestamp, lang);
  const layout = WIDE_LAYOUT_CLASSES[wideLayoutBreakpoint];
  const productCardThemeClasses = getProductCardClasses(theme);
  const isProductHero = heroVariant === EnumFlexibleHeroBlockHeroType.Product;
  const HeadingTag = isPageHeadline ? 'h1' : 'div';
  const productHeroData = isProductHero
    ? mapProductHeroData({
        brand,
        product,
        headlineOverride: headline,
        bodyCopyOverride: bodyCopy,
        keyStats,
        lang,
        productMetaConfig: getPageProductMetaConfig(pageData?.page?.components),
      })
    : undefined;
  const promoCode = productHeroData?.promoCode;
  const resolvedHeadline = productHeroData?.headline ?? headline;
  const resolvedBodyCopy = productHeroData?.bodyCopy ?? bodyCopy;
  const shouldCenterContent = alignment.isCenter;
  const metaSectionClasses = classNames(
    'flex w-full flex-col gap-[--spacing-gap-y]',
    shouldCenterContent ? 'items-center text-center' : 'items-start text-left',
  );
  const paymentsWrapperClasses = classNames(
    'flex w-full',
    isProductHero
      ? classNames('flex-row flex-wrap items-center', layout.productPaymentsGap)
      : 'flex-wrap items-center gap-[--spacing-gap-soft-4px]',
    isProductHero &&
      (shouldCenterContent
        ? 'justify-center text-center'
        : 'justify-start text-left'),
    shouldCenterContent && !isProductHero && 'justify-center',
  );
  const paymentsLabelClasses = classNames(
    textClasses.secondary,
    isProductHero ? layout.productPaymentsLabel : 'mhg-alpha-typography-body-1',
    shouldCenterContent && 'text-center',
  );
  const paymentsTagsClasses = classNames(
    'flex flex-wrap',
    isProductHero
      ? classNames('w-auto', layout.productPaymentsGap)
      : 'w-auto gap-[--spacing-gap-soft-4px]',
    isProductHero && (shouldCenterContent ? 'justify-center' : 'justify-start'),
  );
  const supportedPaymentSections = [
    {
      label: translations.supported_payment_network,
      values: productHeroData?.paymentNetworks ?? [],
    },
    {
      label: translations.supported_payment_type,
      values: productHeroData?.paymentTypes ?? [],
    },
  ].filter((section) => section.values.length > 0);
  const promoWrapperClasses = classNames(
    'relative flex w-full items-center',
    isProductHero
      ? 'flex-row flex-wrap gap-[4px]'
      : 'flex-wrap gap-[--spacing-gap-soft-4px]',
    isProductHero &&
      (shouldCenterContent
        ? 'justify-center text-center'
        : 'justify-start text-left'),
    shouldCenterContent && !isProductHero && 'justify-center',
  );
  const promoLabelClasses = classNames(
    textClasses.secondary,
    isProductHero ? layout.productPromoText : 'mhg-alpha-typography-caption',
  );
  const promoValueClasses = classNames(
    textClasses.promoCode,
    isProductHero ? layout.productPromoText : 'mhg-alpha-typography-bullet-1',
  );

  const sideContent = NonCampaignSideContent({
    heroVariant,
    imageDesktop,
    imageMobile,
    headline: resolvedHeadline,
    lang,
    product,
    productCallToAction,
    productHref,
    ctaLinkOverride,
    productHeroData,
    provider,
    providerCallToAction,
    onApplyClick: handleApplyClick,
    layout,
    productCardThemeClasses,
    textClasses,
  });
  const hasSideContent = Boolean(sideContent);

  const isProductOrProviderHero = [
    EnumFlexibleHeroBlockHeroType.Product,
    EnumFlexibleHeroBlockHeroType.Provider,
  ].includes(heroVariant);

  const highlightTexts = (highlights ?? [])
    .map((item) => item?.text?.trim())
    .filter(Boolean) as string[];
  const hasHighlights = highlightTexts.length > 0;
  const breadcrumb = showBreadcrumbs ? (
    <FlexibleHeroBlockBreadcrumb
      darkmode={theme === EnumFlexibleHeroBlockTheme.Dark}
    />
  ) : null;

  const sideContentMobile = hasSideContent ? (
    <div
      className={classNames(
        'flex w-full flex-col items-center gap-[--spacing-gap-y-sm]',
        layout.stackedSideContent,
      )}
    >
      {NonCampaignSideContent({
        heroVariant,
        imageDesktop,
        imageMobile,
        headline: resolvedHeadline,
        lang,
        product,
        productCallToAction,
        productHref,
        ctaLinkOverride,
        productHeroData,
        provider,
        providerCallToAction,
        onApplyClick: handleApplyClick,
        layout,
        productCardThemeClasses,
        textClasses,
      })}
    </div>
  ) : null;

  const sideContentDesktop = hasSideContent ? (
    <aside
      className={classNames(
        'hidden w-full shrink-0 flex-col items-start gap-spacing/gap/y-sm',
        layout.asideSideContent,
        isProductOrProviderHero
          ? layout.asideProductWidth
          : layout.asideDefaultWidth,
      )}
    >
      {NonCampaignSideContent({
        heroVariant,
        imageDesktop,
        imageMobile,
        headline: resolvedHeadline,
        lang,
        product,
        productCallToAction,
        productHref,
        ctaLinkOverride,
        productHeroData,
        provider,
        providerCallToAction,
        onApplyClick: handleApplyClick,
        layout,
        productCardThemeClasses,
        textClasses,
      })}
    </aside>
  ) : null;

  return (
    <div
      className={classNames('flex w-full flex-col gap-[--spacing-gap-y-sm]', {
        [layout.narrowRootMaxWidth]: !hasSideContent,
        'mx-auto': alignment.isCenter && !hasSideContent,
      })}
      data-component="flexible-hero-block-modern-layout"
    >
      <div
        className={classNames(
          'flex w-full flex-col gap-[--spacing-gap-y-sm]',
          hasSideContent && layout.contentRow,
        )}
      >
        <div
          className={classNames(
            'flex w-full flex-col gap-[24px]',
            shouldCenterContent
              ? 'items-center text-center'
              : 'items-start text-left',
          )}
        >
          <div
            className={classNames(
              'flex w-full flex-col gap-[--spacing-gap-y-sm]',
              shouldCenterContent
                ? 'items-center text-center'
                : 'items-start text-left',
            )}
          >
            {breadcrumb}

            {overline && (
              <div
                className={classNames(
                  'mhg-alpha-typography-title-2',
                  textClasses.overline,
                )}
              >
                {overline}
              </div>
            )}

            {(!!resolvedHeadline || !!timestamp) && (
              <div
                className={classNames(
                  'flex w-full flex-col gap-[var(--spacing-gap-y-xs,8px)]',
                  shouldCenterContent
                    ? 'items-center text-center'
                    : 'items-start text-left',
                )}
              >
                {resolvedHeadline && (
                  // The Flexible Hero block replaces the page's default
                  // PageHeader (see PageContent `shouldRenderDefaultHero`), so
                  // its headline must carry the page's single <h1> for SEO.
                  // Only the page-level hero renders <h1>; stacked
                  // heroes fall back to <div>. Typography matches
                  // ThePageHeader's <h1>.
                  <HeadingTag
                    id={headingId}
                    className={classNames(
                      isProductHero
                        ? layout.productHeadline
                        : 'mhg-alpha-typography-display-3',
                      textClasses.headline,
                    )}
                  >
                    {resolvedHeadline}
                  </HeadingTag>
                )}
                {timestamp && (
                  <div
                    className={classNames(
                      'mhg-alpha-typography-caption',
                      textClasses.body,
                      shouldCenterContent ? 'text-center' : 'text-left',
                    )}
                  >
                    {timestamp}
                  </div>
                )}
              </div>
            )}
          </div>

          {sideContentMobile}

          {hasHighlights && !isProductHero && (
            <div>
              <ul
                className={classNames(
                  'mhg-alpha-typography-bullet-1 list-disc space-y-2 pl-5 text-left',
                  textClasses.body,
                  alignment.isCenter && 'mx-auto',
                )}
              >
                {highlightTexts.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {resolvedBodyCopy && (
            <div
              className={classNames(
                isProductHero
                  ? 'mhg-alpha-typography-body-2-relaxed w-full'
                  : 'mhg-alpha-typography-body-1-relaxed w-full',
                layout.bodyMaxWidth,
                textClasses.body,
                isProductHero && layout.productBodyVisibility,
                shouldCenterContent ? 'mx-auto text-center' : 'text-left',
              )}
            >
              {isProductHero ? (
                <RichText
                  className={classNames(
                    'mhg-alpha-typography-body-2-relaxed lg:mhg-alpha-typography-body-1-relaxed w-full',
                    shouldCenterContent ? 'text-center' : 'text-left',
                  )}
                  baseTextStyle="none"
                >
                  {resolvedBodyCopy}
                </RichText>
              ) : (
                <WrappedText
                  showMoreButtonVisible={true}
                  showMoreButtonText={tr('show_more', lang).toLowerCase()}
                >
                  {resolvedBodyCopy}
                </WrappedText>
              )}
            </div>
          )}

          <div className={metaSectionClasses}>
            {supportedPaymentSections.map(({ label, values }) => (
              <div key={label} className={paymentsWrapperClasses}>
                <span className={paymentsLabelClasses}>{label}:</span>
                <div className={paymentsTagsClasses}>
                  {getPaymentIconBadges(values).map((payment) => (
                    <span
                      key={payment.key}
                      aria-label={payment.label}
                      title={payment.label}
                      className={classNames(
                        'mhg-alpha-typography-bullet-2 inline-flex min-h-[22px] items-center',
                        !payment.icon &&
                          !payment.logo &&
                          'rounded-radius/button px-spacing/padding/soft-8px py-spacing/padding/soft-4px bg-fill/primary-light text-fill/primary-dark',
                      )}
                    >
                      {payment.logo ? (
                        <CmsImage
                          src={payment.logo.url}
                          alt=""
                          width={payment.logo.width ?? 48}
                          height={payment.logo.height ?? 14}
                          sizes={{ _: 48 }}
                          className="h-[14px] w-auto"
                        />
                      ) : payment.icon ? (
                        <Icon
                          icon={payment.icon}
                          aria-hidden="true"
                          className="h-[14px] w-auto"
                        />
                      ) : (
                        payment.label
                      )}
                    </span>
                  ))}
                </div>
              </div>
            ))}

            {promoCode ? (
              <div className={promoWrapperClasses}>
                <span className={promoLabelClasses}>
                  {tr('promo_code', lang)}:
                </span>
                <span className={promoValueClasses}>{promoCode}</span>
                <button
                  type="button"
                  aria-label={tr('promo_code', lang)}
                  className={classNames(
                    'flex items-center justify-center',
                    textClasses.promoCopyIcon,
                    layout.promoCopyButtonSize,
                  )}
                  onClick={() => promoCodeCopy.copy(promoCode)}
                >
                  <ContentCopy className="cursor-pointer" />
                </button>
                {promoCodeCopy.isCopied ? (
                  <span className="mhg-alpha-typography-caption rounded-[--radius-button] px-2 py-1 shadow-md bg-[--fill-card] text-[--text-secondary-lighter]">
                    {tr('copied', lang)}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>

          {buttons.length > 0 && !isProductHero && (
            <div className={alignment.ctaWrapper}>
              <CtaButtonComponents buttons={buttons} />
            </div>
          )}
        </div>

        {sideContentDesktop}
      </div>
    </div>
  );
};
